/**
 * Sincronização iCal - Booking.com e Airbnb.
 * Busca URLs iCal, parseia e extrai datas bloqueadas (reservas).
 *
 * IMPORTANTE: URLs do Booking/Airbnb podem bloquear CORS. Nesse caso,
 * é necessário um backend/proxy que faça o fetch e retorne o conteúdo.
 * Alternativa: usar extensão ou serviço que não sofre CORS (ex: serverless function).
 */

function parseICalDate(str: string): Date | null {
  if (!str || typeof str !== 'string') return null;
  const s = str.trim();
  // DTSTART:20240115 ou DTSTART;VALUE=DATE:20240115 ou 20240115T140000Z
  const dateMatch = s.match(/(\d{4})(\d{2})(\d{2})/);
  if (dateMatch) {
    const y = parseInt(dateMatch[1], 10);
    const m = parseInt(dateMatch[2], 10) - 1;
    const d = parseInt(dateMatch[3], 10);
    return new Date(y, m, d);
  }
  return null;
}

/**
 * Parse simples de conteúdo iCal (.ics) - extrai datas de eventos.
 * Cada VEVENT com DTSTART/DTEND representa um período bloqueado.
 */
export function parseIcalToBlockedDates(icsContent: string): string[] {
  const dates = new Set<string>();
  const lines = icsContent.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n');

  let inEvent = false;
  let dtStart: Date | null = null;
  let dtEnd: Date | null = null;

  const flushEvent = () => {
    if (dtStart && dtEnd) {
      const start = new Date(dtStart.getFullYear(), dtStart.getMonth(), dtStart.getDate());
      const end = new Date(dtEnd.getFullYear(), dtEnd.getMonth(), dtEnd.getDate());
      const d = new Date(start);
      while (d < end) {
        dates.add(d.toISOString().slice(0, 10));
        d.setDate(d.getDate() + 1);
      }
    }
    dtStart = null;
    dtEnd = null;
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    let value = line;
    // Unfold long lines (continuation with space/tab)
    while (i + 1 < lines.length && /^[ \t]/.test(lines[i + 1])) {
      value += lines[++i].replace(/^[ \t]/, '');
    }

    if (value.startsWith('BEGIN:VEVENT')) {
      inEvent = true;
      dtStart = null;
      dtEnd = null;
      continue;
    }
    if (value.startsWith('END:VEVENT')) {
      flushEvent();
      inEvent = false;
      continue;
    }
    if (!inEvent) continue;

    if (value.startsWith('DTSTART')) {
      const colon = value.indexOf(':');
      const val = colon >= 0 ? value.slice(colon + 1) : '';
      dtStart = parseICalDate(val);
    }
    if (value.startsWith('DTEND')) {
      const colon = value.indexOf(':');
      const val = colon >= 0 ? value.slice(colon + 1) : '';
      dtEnd = parseICalDate(val);
    }
  }
  flushEvent();

  return Array.from(dates).sort();
}

/**
 * Busca conteúdo de uma URL iCal.
 * CORS: Se a URL for de outro domínio e bloquear CORS, falhará.
 * Solução: criar endpoint no backend que faça proxy do fetch.
 */
export async function fetchIcalContent(url: string): Promise<string> {
  const trimmed = (url || '').trim();
  if (!trimmed || !trimmed.startsWith('http')) {
    throw new Error('URL iCal inválida.');
  }
  const res = await fetch(trimmed, { method: 'GET' });
  if (!res.ok) {
    throw new Error(`Erro ao buscar iCal: ${res.status}`);
  }
  return res.text();
}

/**
 * Combina múltiplas URLs iCal e retorna todas as datas bloqueadas.
 * Tenta API proxy primeiro (evita CORS); fallback para fetch direto.
 */
export async function fetchBlockedDatesFromUrls(urls: string[]): Promise<string[]> {
  const validUrls = urls.filter((u) => (u || '').trim().startsWith('http'));
  if (validUrls.length === 0) return [];

  // 1. Tenta API proxy (evita CORS com Booking/Airbnb)
  try {
    const { apiUrl } = await import('./api');
    const res = await fetch(apiUrl('/api/admin/sync-ical'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ urls: validUrls }),
    });
    if (res.ok) {
      const data = (await res.json()) as { blockedDates?: string[] };
      if (Array.isArray(data.blockedDates)) return data.blockedDates.sort();
    }
  } catch {
    // API indisponível; tenta fetch direto
  }

  // 2. Fallback: fetch direto (pode falhar por CORS)
  const allDates = new Set<string>();
  for (const url of validUrls) {
    try {
      const content = await fetchIcalContent(url);
      const dates = parseIcalToBlockedDates(content);
      dates.forEach((d) => allDates.add(d));
    } catch (e) {
      console.warn('Erro ao buscar iCal:', url, e);
      throw e;
    }
  }
  return Array.from(allDates).sort();
}
